import { computed, makeObservable, observable, runInAction } from 'mobx';

import { LoadState } from 'app/loading/LoadState';

/**
 * Метод для изменения статуса загрузки в callback-е.
 */
type ChangeState = (state: LoadState) => void;

/**
 * Callback для загрузки данных.
 */
type LoadDataCallback = (changeState?: ChangeState) => Promise<any>;

/**
 * Store для отслеживания статуса загрузки данных.
 */
export class LoadStateStore {
    @observable
    private _state: LoadState;

    private readonly includeLoadingState: boolean;

    constructor(includeLoadingState: boolean = false) {
        makeObservable(this);
        this._state = LoadState.NOT_LOADED;
        this.includeLoadingState = includeLoadingState;
        makeObservable(this);
    }

    /**
     * Вызывает callback загрузки данных.
     *
     * Изменяет статус в зависимости от состояния загрузки. Данные считаются успешно загруженными,
     * если в течении их загрузки не возникло исключения.
     *
     * Статус можно обновить вручную при помощи метода changeState передаваемого в callback.
     * Может пригодиться в случаях, когда исключения обрабатываются внутри callback и не пробрасываются наверх.
     *
     * @param callback функция для загрузки данных
     */
    loadData(callback: LoadDataCallback): Promise<any> {
        if (this.includeLoadingState) {
            runInAction(() => (this._state = LoadState.LOADING));
        }

        return callback()
            .then((data) => {
                runInAction(() => (this._state = LoadState.LOADED));
                return data;
            })
            .catch((error) => {
                runInAction(() => (this._state = LoadState.HAS_ERROR));
                throw error;
            });
    }

    /**
     * Произошла ошибка при загрузке данных.
     */
    @computed
    get hasError(): boolean {
        return this._state === LoadState.HAS_ERROR;
    }

    /**
     * Данные были успешно загружены.
     */
    @computed
    get isLoaded(): boolean {
        return this._state === LoadState.LOADED;
    }

    /**
     * Данные загружаются.
     */
    @computed
    get isLoading(): boolean {
        return this._state === LoadState.LOADING;
    }

    @computed
    get isNotLoaded(): boolean {
        return this._state === LoadState.NOT_LOADED;
    }
}
